import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { toast } from 'react-hot-toast';

import { useWebSocket } from './WebSocketProvider'; // Assumes WebSocketProvider is in the same folder
import { DataState } from './DataProvider'; // Assumes DataProvider is in the same folder
import { getNotifications } from '../services/workflowApi'; // Import the API function you created
// Add XIcon to your lucide-react imports, for example:
import { createTask } from '../services/api';

// 1. Create the context
const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { subscribe, isConnected } = useWebSocket();
  const {
    userGoogleId: accessToken,
    currentTaskId,
    setCurrentTaskId,
    setTasks,
    dispatchConversations,
    setInput,
    setTaskInput,
    setNewTaskSubmitted,
    setError,
    setCreateTaskLoading,
    setIsBusy,
  } = DataState(); // Get the access token from your main DataProvider
  const [notifications, setNotifications] = useState([]);
  // const [notifications, setNotifications] = useState(sampleNotifications);

  // from data provider

  // --- NEW: Add state for in-chat notifications ---
  // const [openNotifications, setOpenNotifications] = useState([]);
  // We no longer store a separate array of open notification objects.
  // Instead, we only store the IDs of notifications that are "open" in the current task context.
  const [openNotificationIds, setOpenNotificationIds] = useState(new Set());
  const [activeNotificationId, setActiveNotificationId] = useState(null);
  const [collapsedNotificationIds, setCollapsedNotificationIds] = useState(
    new Set(),
  );
  const [pendingNotificationForNewTask, setPendingNotificationForNewTask] =
    useState(null);

  // ✅ 2. DERIVED STATE: `openNotifications` is now derived from the master list and the set of IDs.
  // This guarantees it is always up-to-date.
  const openNotifications = useMemo(
    () => notifications.filter((n) => openNotificationIds.has(n.id)),
    [notifications, openNotificationIds],
  );

  useEffect(() => {
    if (currentTaskId) {
      if (pendingNotificationForNewTask) {
        // setOpenNotifications([pendingNotificationForNewTask]);
        // Instead of setting the array, we set the ID in the new Set.
        setOpenNotificationIds(new Set([pendingNotificationForNewTask.id]));

        setActiveNotificationId(pendingNotificationForNewTask.id);
        setCollapsedNotificationIds(new Set());
        // Clear the pending state so it only runs once
        setPendingNotificationForNewTask(null);
      } else {
        // If no pending notification, just reset the state as before
        // setOpenNotifications([]);
        // If no pending notification, clear the Set of IDs.
        setOpenNotificationIds(new Set());
        setActiveNotificationId(null);
        setCollapsedNotificationIds(new Set());
      }
    }
    // eslint-disable-next-line
  }, [currentTaskId]);

  const createNewTaskForNotification = async (
    notificationPayload,
    userPrompt = '',
  ) => {
    if (!notificationPayload) return;

    setCreateTaskLoading(true);
    setIsBusy(true);

    try {
      function stripTags(str = '') {
        return str.replace(/<\/?[^>]+(>|$)/g, ''); // remove HTML tags
      }

      // const safeNotification = {
      //   ...notification,
      //   data: {
      //     ...notification.data,
      //     // Only clean if body exists
      //     ...(notification.data?.body
      //       ? { body: stripTags(notification.data.body) }
      //       : {}),
      //   },
      // };
      // Sanitize the notification payload, especially HTML in email bodies
      let eventData = '';
      if (notificationPayload.source_type === 'listener') {
        // Handle other source types
        const safeNotification = {
          ...notificationPayload,
          listener_data: {
            ...notificationPayload.listener_data,
            payload: {
              ...notificationPayload.listener_data.payload,
              ...(notificationPayload.listener_data.payload?.body
                ? {
                    body: stripTags(
                      notificationPayload.listener_data.payload.body,
                    ),
                  }
                : {}),
            },
          },
        };
        eventData = `<event event="${safeNotification.event_name}" app="${safeNotification.listener_data.app}">${JSON.stringify(safeNotification)}</event>`;
      } else if (notificationPayload.source_type === 'meeting') {
        // Handle Teams meeting notifications
        const meetingEvent = {
          subject: notificationPayload.subject,
          organizer: notificationPayload.organizer,
          attendees: notificationPayload.attendees,
          start: notificationPayload.start,
          end: notificationPayload.end,
          description: stripTags(notificationPayload.description || ''),
          status: notificationPayload.transcript_status,
          last_updated: notificationPayload.timestamp,
          ...(notificationPayload.transcriptContent
            ? { transcriptContent: notificationPayload.transcriptContent }
            : {}),
          ...(notificationPayload.llm_summary
            ? { llm_summary: notificationPayload.llm_summary }
            : {}),
        };
        eventData = `<event event="${notificationPayload.event_name}" app="${notificationPayload.app}" event_id="${notificationPayload.event_id}" meeting_id="${notificationPayload.meeting_id}">${JSON.stringify(meetingEvent, null, 2)}</event>`;
        //         eventData = `
        //   <event
        //     event="${notificationPayload.event_name}"
        //     app="${notificationPayload.app}"
        //   >
        //     ${JSON.stringify(notificationPayload, null, 2)}
        //   </event>
        // `;
      } else if (notificationPayload.source_type === 'change_request') {
        const cr = notificationPayload.cr_data || notificationPayload;
        const crPayload = {
          id: cr.id,
          title: cr.title,
          raisedBy: cr.raisedBy,
          date: cr.date,
          classification: cr.classification,
          phase: cr.phase,
          source_origin: cr.source_origin,
          ...(cr.assumption ? { assumption: cr.assumption } : {}),
          ...(cr.sourceDoc ? { sourceDoc: cr.sourceDoc } : {}),
          metrics: cr.metrics,
          ...(cr.impact ? { impact: cr.impact } : {}),
          ...(notificationPayload.section_key
            ? {
                active_section: notificationPayload.section_key,
                section_label: notificationPayload.section_label,
                section_data: notificationPayload.section_data,
              }
            : {}),
        };
        const sectionAttr = notificationPayload.section_label
          ? ` section="${notificationPayload.section_label}"`
          : '';
        eventData = `<change_request id="${cr.id}" event="change_request_context"${sectionAttr}>${JSON.stringify(crPayload, null, 2)}</change_request>`;
      } else {
        return;
      }

      // Create the structured input for the agent
      const newInput = `${eventData}\n\n${userPrompt.trim()}`;

      // Create the task with a descriptive name
      const taskDescription = notificationPayload.section_label
        ? `Event: ${notificationPayload.event_name} — ${notificationPayload.section_label}`
        : `Event: ${notificationPayload.event_name}`;
      const data = await createTask({ task: taskDescription }, accessToken);

      if (data.error) throw new Error(data.error);

      const newTask = {
        _id: data.task_id,
        task_description: taskDescription,
        tools: data.tools,
      };

      setTasks((prevTasks) => [newTask, ...prevTasks]);

      // This is the fix: Set the pending notification BEFORE changing the task ID
      setPendingNotificationForNewTask(notificationPayload);
      // Now, changing the task ID will trigger the useEffect to correctly open it
      setCurrentTaskId(data.task_id);
      dispatchConversations({ type: 'TASK_SWITCHED' });

      setInput(newInput);
      setCurrentTaskId(data.task_id);
      setNewTaskSubmitted(true);
      setTaskInput('');

      setError(null);

      // Close all open notification views after submission
      setActiveNotificationId(null);
      // setOpenNotifications();
      setOpenNotificationIds(new Set());
      setCollapsedNotificationIds(new Set());
    } catch (error) {
      toast.error('Error creating new agent for notification.');
      setError('Error creating new agent for notification.');
    } finally {
      setCreateTaskLoading(false);
      setIsBusy(false);
    }
  };

  // ✅ NEW: Opens a notification by adding its ID to the context.
  const openNotificationInContext = (notificationId) => {
    setOpenNotificationIds((prev) => new Set(prev).add(notificationId));
  };

  // ✅ NEW: Closes a notification by removing its ID from all relevant states.
  const closeNotificationInContext = (notificationId) => {
    setOpenNotificationIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(notificationId);
      return newSet;
    });
    setCollapsedNotificationIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(notificationId);
      return newSet;
    });
    if (activeNotificationId === notificationId) {
      setActiveNotificationId(null);
    }
  };

  // // --- NEW: Helper functions for managing notification state ---
  // const addOpenNotification = (notification) => {
  //   setOpenNotifications(prev => {
  //     // Add notification only if it's not already in the list
  //     if (prev.find(n => n.id === notification.id)) {
  //       return prev;
  //     }
  //     return [...prev, notification];
  //   });
  // };

  const removeCollapsedNotificationId = (notificationId) => {
    setCollapsedNotificationIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(notificationId);
      return newSet;
    });
  };

  // Notification provider original logic

  // 2. Handle incoming WebSocket notifications
  const handleNewNotification = useCallback((notification) => {
    setNotifications((prevNotifications) => {
      const newNotifications = [...prevNotifications];

      // Check if this notification is an update to an existing one
      // const existingIndex = newNotifications.findIndex(n => n._id === notification._id);
      const existingIndex = newNotifications.findIndex(
        (n) => n.id === notification.id,
      );

      if (existingIndex !== -1) {
        // --- This is an UPDATE ---
        // Replace the old notification object with the new one
        newNotifications[existingIndex] = {
          ...newNotifications[existingIndex],
          ...notification,
        };

        // Optional: Show a toast for the update
        // toast.info(`Notification updated: ${notification.event_name}`);
      } else {
        // --- This is a NEW notification ---
        // Add the new notification to the top of the list
        newNotifications.unshift(notification);

        // Show a toast for the new notification
        toast(notification.event_name, {
          icon: '🔔', // Add a bell icon
        });
      }

      // 3. Pruning logic: Keep the list from growing too large.
      // If the list is over 50, remove the oldest inactive notification.
      if (newNotifications.length > 50) {
        let removed = false;
        for (let i = newNotifications.length - 1; i >= 0; i--) {
          if (newNotifications[i].active === false) {
            newNotifications.splice(i, 1); // Remove the oldest inactive one
            removed = true;
            break;
          }
        }
        // If all are active, just remove the oldest one
        if (!removed) {
          newNotifications.pop();
        }
      }

      return newNotifications;
    });
  }, []);

  // ✅ NEW: Function to update a specific notification in the state
  const updateNotification = useCallback((notificationId, updatedData) => {
    setNotifications((prevNotifications) =>
      prevNotifications.map((n) =>
        // Find the notification by its ID and merge the new data
        n.id === notificationId ? { ...n, ...updatedData } : n,
      ),
    );
  }, []);

  const upsertNotification = useCallback((notification) => {
    if (!notification?.id) return;

    setNotifications((prevNotifications) => {
      const existing = prevNotifications.find((n) => n.id === notification.id);
      const remaining = prevNotifications.filter(
        (n) => n.id !== notification.id,
      );

      return [
        existing ? { ...existing, ...notification } : notification,
        ...remaining,
      ];
    });
  }, []);

  const addNotification = useCallback((notification) => {
    setNotifications((prev) => {
      if (prev.find((n) => n.id === notification.id)) return prev;
      return [notification, ...prev];
    });
  }, []);

  const removeNotification = useCallback((notificationId) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
  }, []);

  const removeMultipleNotifications = useCallback((notificationIds) => {
    setNotifications((prev) =>
      prev.filter((n) => !notificationIds.includes(n.id)),
    );
  }, []);

  // 4. Subscribe to WebSocket events
  useEffect(() => {
    // Only subscribe if we are connected and authenticated
    if (!isConnected || !accessToken) return;

    // Define the event key(s) your WebSocket server will emit
    const eventKey = 'app_notification_new';
    const updateEventKey = 'app_notification_update';
    const removeEventKey = 'app_notifications_remove';

    const subscriptions = {
      [eventKey]: (data) => handleNewNotification(data),
      [updateEventKey]: (data) => handleNewNotification(data), // Handle updates the same way
      [removeEventKey]: (data) => removeMultipleNotifications(data.ids || []), // Remove notifications by IDs
    };

    // Subscribe and get the cleanup function
    const unsubscribe = subscribe(subscriptions);

    // Cleanup on unmount or when dependencies change
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line
  }, [isConnected, accessToken, subscribe, handleNewNotification]);

  // 5. Fetch initial notifications from the API on load
  useEffect(() => {
    if (!accessToken) return; // Don't fetch without an access token

    const fetchInitialNotifications = async () => {
      try {
        // Call your API function (using default options: active_only=true, limit=20)
        const response = await getNotifications(accessToken, {
          active_only: true,
          skip: 0,
          limit: 50, // Fetch up to 50 active notifications on load
        });

        if (response.notifications) {
          setNotifications(response.notifications);
        }
      } catch (error) {
        toast.error('Could not load notifications.');
      }
    };

    fetchInitialNotifications();
  }, [accessToken]); // Re-fetch if the access token changes

  // Provide the state and functions to children
  const contextValue = {
    notifications,
    addNotification,
    upsertNotification,
    removeNotification,
    updateNotification,
    // You can also add setNotifications if other components need to modify the list
    // setNotifications,

    // shifted from DataProvider context
    openNotifications,
    // setOpenNotifications,
    activeNotificationId,
    setActiveNotificationId,
    collapsedNotificationIds,
    setCollapsedNotificationIds,
    // addOpenNotification,
    // removeCollapsedNotificationId,
    // createNewTaskForNotification,

    // Actions
    openNotificationInContext,
    closeNotificationInContext,
    removeCollapsedNotificationId,
    createNewTaskForNotification,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

// 7. Custom hook for easy consumption
export const useNotifications = () => useContext(NotificationContext);
